import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import '../styles/Content.css';
import ProfileImage from './ProfileImage';
import CommandBar from './CommandBar';
import SocialLinks from './SocialLinks';
import Projects from './Projects';
import Description from './Description';
import Contact from './Contact';
import Resume from './Resume';
import { consumePendingNavContext, setPendingNavContext, track } from '../lib/analytics';

const normalizeSection = (raw) => {
  const cleaned = String(raw || '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase();
  const allowed = new Set(['home', 'projects', 'about', 'contact', 'resume', 'education', 'languages']);
  if (allowed.has(cleaned)) return cleaned;
  return 'home';
};

const COMMAND_ITEMS = [
  { label: 'Projects', cmd: '/projects', section: 'projects' },
  { label: 'About', cmd: '/about', section: 'about' },
  { label: 'Contact', cmd: '/contact', section: 'contact' },
  { label: 'Work Experience', cmd: '/resume', section: 'resume' },
  { label: 'Education', cmd: '/education', section: 'education' },
  { label: 'Languages', cmd: '/languages', section: 'languages' },
  { label: 'Home', cmd: '/home', section: 'home' },
];

const Content = () => {
  const [activeSection, setActiveSection] = useState(() => normalizeSection(window.location.hash));
  const [focusNonce, setFocusNonce] = useState(0);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const stageRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const commandsMenuRef = useRef(null);
  const prevSectionRef = useRef(null);

  useEffect(() => {
    const handleHashChange = () => setActiveSection(normalizeSection(window.location.hash));
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const prev = prevSectionRef.current;
    const ctx = consumePendingNavContext();

    track('section_view', {
      section: activeSection,
      from_section: prev,
      source: ctx?.source || (prev ? 'hash' : 'load'),
      surface: ctx?.surface,
      trigger: ctx?.trigger,
      nav_item: ctx?.nav_item,
      command: ctx?.command,
    });

    prevSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const handleFocusRequest = (e) => {
      const requested = normalizeSection(e?.detail?.section);
      if (requested !== activeSection) return;
      setFocusNonce((n) => n + 1);
    };

    window.addEventListener('ipavlidakis:focus', handleFocusRequest);
    return () => window.removeEventListener('ipavlidakis:focus', handleFocusRequest);
  }, [activeSection]);

  useEffect(() => {
    // Close the dropdown on navigation (hash change, command, nav click).
    setCommandsOpen(false);
  }, [activeSection]);

  useEffect(() => {
    if (!commandsOpen) return;

    const handlePointerDown = (e) => {
      const root = commandsMenuRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setCommandsOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setCommandsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [commandsOpen]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    // On initial load, don't auto-scroll the default (home) view.
    if (!hasFocusedRef.current) {
      hasFocusedRef.current = true;
      if (activeSection === 'home' && focusNonce === 0) return;
    }

    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    // Restart the flash animation even if the section did not change.
    el.classList.remove('is-flashing');
    void el.offsetWidth;
    el.classList.add('is-flashing');
  }, [activeSection, focusNonce]);

  const navigateToSection = (section, meta = {}) => {
    const next = normalizeSection(section);
    if (next === activeSection) {
      track('section_refocus', {
        section: next,
        source: meta.source,
        surface: meta.surface,
        trigger: meta.trigger,
        nav_item: meta.nav_item,
        command: meta.command,
      });
      setFocusNonce((n) => n + 1);
      return;
    }

    setPendingNavContext({
      source: meta.source,
      surface: meta.surface,
      trigger: meta.trigger,
      nav_item: meta.nav_item,
      command: meta.command,
    });
    setActiveSection(next);
    window.location.hash = `#${next}`;
  };

  const runCommand = async (cmd, meta = {}) => {
    const mapping = {
      '/home': 'home',
      '/projects': 'projects',
      '/about': 'about',
      '/contact': 'contact',
      '/resume': 'resume',
      '/education': 'education',
      '/languages': 'languages',
    };

    const nextSection = mapping[cmd] || null;
    const recognized = Boolean(nextSection);

    // Avoid logging arbitrary input as analytics params (PII risk).
    track('command_run', {
      recognized,
      command: recognized ? cmd : undefined,
      from_section: activeSection,
      to_section: nextSection,
      surface: meta.surface,
      trigger: meta.trigger,
      item_label: meta.item_label,
      card: meta.card,
    });

    if (recognized) {
      navigateToSection(nextSection, {
        source: 'command',
        surface: meta.surface,
        trigger: meta.trigger,
        command: cmd,
      });
      return '';
    }

    return 'Try /projects, /about, /contact, /home, /resume, /education, or /languages.';
  };

  const activeCommand = useMemo(() => {
    const mapping = {
      home: '/home',
      projects: '/projects',
      about: '/about',
      contact: '/contact',
      resume: '/resume',
      education: '/education',
      languages: '/languages',
    };
    return mapping[activeSection] || '';
  }, [activeSection]);

  return (
    <main className="page-main">
      <section id="home" className="hero">
        <ProfileImage />

        <h1 className="hero-title">Ilias Pavlidakis</h1>

        <p className="hero-lede">
          I build scalable, high-performance systems. I focus on shipping pragmatic solutions,
          performance work, and mentoring teams.
        </p>

        <CommandBar onCommand={runCommand} />

        <div className="hero-hints" aria-label="Command hints">
          <span className="hero-hints-core">
            Try:{' '}
            <button
              className="hint"
              type="button"
              onClick={() => runCommand('/projects', { surface: 'hero_hint', trigger: 'click' })}
            >
              /projects
            </button>
            ,{' '}
            <button
              className="hint"
              type="button"
              onClick={() => runCommand('/resume', { surface: 'hero_hint', trigger: 'click' })}
            >
              /resume
            </button>
            ,{' '}
            <button
              className="hint"
              type="button"
              onClick={() => runCommand('/contact', { surface: 'hero_hint', trigger: 'click' })}
            >
              /contact
            </button>
          </span>

          <div className="hint-menu" ref={commandsMenuRef}>
            <button
              className={`hint-menu-button ${commandsOpen ? 'is-open' : ''}`}
              type="button"
              aria-haspopup="menu"
              aria-expanded={commandsOpen}
              onClick={() => {
                const next = !commandsOpen;
                setCommandsOpen(next);
                track('commands_menu_toggle', {
                  surface: 'hero_commands_menu',
                  is_open: next,
                  active_section: activeSection,
                });
              }}
            >
              Commands
              <span className="hint-menu-chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
            </button>

            {commandsOpen ? (
              <div className="hint-menu-popover" role="menu" aria-label="All commands">
                {COMMAND_ITEMS.map((item) => (
                  <button
                    key={item.cmd}
                    className={`hint-menu-item ${
                      activeCommand === item.cmd ? 'is-selected' : ''
                    }`}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setCommandsOpen(false);
                      runCommand(item.cmd, {
                        surface: 'hero_commands_menu',
                        trigger: 'click',
                        item_label: item.label,
                      });
                    }}
                  >
                    <span className="hint-menu-item-label">{item.label}</span>
                    <span className="hint-menu-item-cmd">{item.cmd}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <SocialLinks variant="hero" limit={4} activeSection={activeSection} />
      </section>

      <div className="stage" ref={stageRef}>
        {activeSection === 'home' ? (
          <section className="cards" aria-label="Highlights">
            <div className="card">
              <div className="card-eyebrow">SELECTED WORK</div>
              <p className="card-text">Project highlights with concise, “scan-able” summaries.</p>
              <div className="card-actions">
                <button
                  className="card-link"
                  type="button"
                  onClick={() =>
                    runCommand('/projects', { surface: 'home_card', trigger: 'click', card: 'selected_work' })
                  }
                >
                  Open /projects
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-eyebrow">WHAT I DO</div>
              <p className="card-text">Performance, architecture, and high-signal mentoring.</p>
              <ul className="mini-list" aria-label="What I do">
                <li>Scale products without over-engineering</li>
                <li>Make latency and UX feel fast</li>
                <li>Grow teams through feedback and systems</li>
              </ul>
            </div>

            <div className="card">
              <div className="card-eyebrow">CONTACT</div>
              <p className="card-text">Everything reachable from one clean surface.</p>
              <div className="card-actions">
                <button
                  className="card-link"
                  type="button"
                  onClick={() =>
                    runCommand('/contact', { surface: 'home_card', trigger: 'click', card: 'contact' })
                  }
                >
                  Open /contact
                </button>
              </div>
            </div>
          </section>
        ) : (
          <div className="stage-panel">
            {activeSection === 'projects' ? <Projects /> : null}
            {activeSection === 'about' ? <Description /> : null}
            {activeSection === 'contact' ? <Contact /> : null}
            {activeSection === 'resume' ? <Resume mode="work" /> : null}
            {activeSection === 'education' ? <Resume mode="education" /> : null}
            {activeSection === 'languages' ? <Resume mode="languages" /> : null}
          </div>
        )}
      </div>
    </main>
  );
};

export default Content;
